/**
 * The commercial buyer's side of a bus purchase: financing inquiry, delivery
 * request, and the documents both depend on.
 *
 * Three concerns in one module because they are one conversation. A school
 * division asks about financing, asks whether the coach can reach the
 * community, and sends the paperwork that answers both — and the delivery
 * answer is also the tax answer, which is why they cannot sensibly live apart.
 *
 * ## What this module is not
 *
 * It is not a second payment system, a second delivery estimator, or a second
 * finance application. The marketplace already has:
 *
 *   - `server/api/delivery-estimate.js`, which prices a route for a
 *     `sedan | suv | truck | other` on an open or enclosed car carrier.
 *   - `server/api-util/financeApplications.js`, the consumer credit path.
 *   - `server/api/stripe-webhook.js`, which is the only thing that grants a
 *     paid entitlement, and is untouched here.
 *
 * **Neither of the first two fits a bus, and using them anyway would produce
 * confident wrong answers.** A 45-foot motorcoach does not go on a car
 * carrier; it is driven under its own power by a qualified driver, or moved by
 * a specialist heavy hauler. Feeding it through the car estimator as `truck`
 * would return a number that looks like a quote and is not one. And a band
 * council or a school division is not a consumer credit file: the entity
 * borrows, not a person, so the consumer application asks all the wrong
 * questions.
 *
 * So this module produces *requests*, never prices and never approvals. Every
 * function here validates and routes. None of them quotes.
 */

import {
  assessFirstNationsTax,
  mustChargeFullTax,
  BUYER_STATUS,
  DELIVERY_LOCATION,
} from './firstNationsTaxAssessment.js';

// ─── Buyer organisations ─────────────────────────────────────────────────────

/**
 * Who is buying. This drives which questions are asked and which documents are
 * required — not whether anyone is trusted.
 */
export const OPERATOR_TYPE = {
  FIRST_NATION: 'first-nation',
  SCHOOL_DIVISION: 'school-division',
  MUNICIPALITY: 'municipality',
  CHARTER_OPERATOR: 'charter-operator',
  PRIVATE_BUSINESS: 'private-business',
  INDIVIDUAL: 'individual',
};

const ORGANISATIONAL = [
  OPERATOR_TYPE.FIRST_NATION,
  OPERATOR_TYPE.SCHOOL_DIVISION,
  OPERATOR_TYPE.MUNICIPALITY,
  OPERATOR_TYPE.CHARTER_OPERATOR,
  OPERATOR_TYPE.PRIVATE_BUSINESS,
];

export const isOrganisationalBuyer = operatorType => ORGANISATIONAL.includes(operatorType);

// ─── Commercial financing inquiry ────────────────────────────────────────────

/**
 * Validate a commercial financing inquiry.
 *
 * An inquiry, not an application. Nothing here runs a credit check, quotes a
 * rate, or creates an obligation — it collects enough for a lending partner to
 * start a conversation, and says plainly that it is not an approval.
 *
 * A personal credit profile is deliberately not collected for an
 * organisational buyer. The entity borrows; asking a band administrator for
 * their own credit score would be both wrong and intrusive.
 *
 * @returns {{valid: Boolean, errors: Array<String>, inquiry: Object|null}}
 */
export const validateFinancingInquiry = ({
  operatorType = null,
  legalName = '',
  contactName = '',
  email = '',
  phone = '',
  province = null,
  amountRequestedCents = null,
  listingId = null,
} = {}) => {
  const errors = [];

  if (!Object.values(OPERATOR_TYPE).includes(operatorType)) {
    errors.push('Select who is buying.');
  }
  if (isOrganisationalBuyer(operatorType) && !String(legalName).trim()) {
    // The entity on the agreement, not a trading name.
    errors.push('The legal name of the organisation is required.');
  }
  if (!String(contactName).trim()) {
    errors.push('A contact name is required.');
  }
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(email).trim())) {
    errors.push('A valid email address is required.');
  }
  if (String(phone).replace(/\D/g, '').length < 10) {
    errors.push('A phone number with at least 10 digits is required.');
  }
  if (!listingId) {
    errors.push('The inquiry must reference a listing.');
  }

  // Money is handled in integer cents everywhere in this codebase; a float
  // here would be a rounding bug waiting for a large number, and a coach is a
  // large number.
  if (amountRequestedCents !== null) {
    if (!Number.isInteger(amountRequestedCents) || amountRequestedCents <= 0) {
      errors.push('The amount requested must be a whole number of cents.');
    }
  }

  if (errors.length) {
    return { valid: false, errors, inquiry: null };
  }

  return {
    valid: true,
    errors: [],
    inquiry: {
      kind: 'commercial-financing-inquiry',
      operatorType,
      legalName: isOrganisationalBuyer(operatorType) ? String(legalName).trim() : null,
      contactName: String(contactName).trim(),
      email: String(email)
        .trim()
        .toLowerCase(),
      phone: String(phone).trim(),
      province: province || null,
      amountRequestedCents: amountRequestedCents ?? null,
      listingId,
      // The three facts that stop this being mistaken for a decision.
      isApproval: false,
      creditCheckPerformed: false,
      disclaimer:
        'An inquiry only. This is not an approval, not an offer of credit, and no credit check has been run. ' +
        'A lending partner will contact you; terms depend on their assessment.',
    },
  };
};

// ─── Canada-wide and remote-community delivery ───────────────────────────────

export const DESTINATION_TYPE = {
  /** Reached on paved highway, year round. */
  HIGHWAY: 'highway',
  /** Reached by gravel or seasonal road; access varies with weather. */
  SEASONAL_ROAD: 'seasonal-road',
  /** Reachable overland only on a winter road, for part of the year. */
  WINTER_ROAD: 'winter-road',
  /** No road access at any time of year. */
  FLY_IN: 'fly-in',
};

export const DELIVERY_METHOD = {
  /** A qualified driver drives the vehicle to the destination. */
  DRIVE_AWAY: 'drive-away',
  /** A specialist heavy hauler moves it on a float. */
  HEAVY_HAUL: 'heavy-haul',
  /** The buyer collects it from the seller. */
  BUYER_COLLECTS: 'buyer-collects',
};

/**
 * Which methods can actually reach a destination.
 *
 * A fly-in community cannot receive a 45-foot coach by road in any season, so
 * no method is offered and the request says so rather than quietly accepting
 * an impossible booking. Barge and winter-road freight exist, but this
 * platform has no carrier relationship that can commit to either, and
 * pretending otherwise would strand a community that planned around it.
 */
const METHODS_BY_DESTINATION = {
  [DESTINATION_TYPE.HIGHWAY]: [
    DELIVERY_METHOD.DRIVE_AWAY,
    DELIVERY_METHOD.HEAVY_HAUL,
    DELIVERY_METHOD.BUYER_COLLECTS,
  ],
  [DESTINATION_TYPE.SEASONAL_ROAD]: [DELIVERY_METHOD.DRIVE_AWAY, DELIVERY_METHOD.BUYER_COLLECTS],
  [DESTINATION_TYPE.WINTER_ROAD]: [DELIVERY_METHOD.DRIVE_AWAY, DELIVERY_METHOD.BUYER_COLLECTS],
  [DESTINATION_TYPE.FLY_IN]: [],
};

export const availableDeliveryMethods = destinationType =>
  METHODS_BY_DESTINATION[destinationType] || [];

const CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

/**
 * Validate a delivery request.
 *
 * Deliberately produces **no cost**. The existing delivery estimator prices
 * cars on car carriers and does not model a coach; a number from it here would
 * read as a quote and be wrong. This records what is being asked for and
 * hands it to a human who can source a real quote.
 *
 * Where delivery happens is also the fact that decides First Nations tax
 * relief, so when the caller says the destination is on a reserve this returns
 * the tax assessment alongside — one answer, not two that can disagree.
 *
 * @returns {{valid: Boolean, errors: Array<String>, request: Object|null}}
 */
export const validateDeliveryRequest = ({
  listingId = null,
  destinationType = null,
  destinationPostalCode = '',
  deliveryMethod = null,
  toReserve = false,
  reserveName = '',
  buyerStatus = BUYER_STATUS.NONE,
  documentsOnFile = false,
  forBandManagement = null,
  province = null,
} = {}) => {
  const errors = [];

  if (!listingId) {
    errors.push('The request must reference a listing.');
  }
  if (!Object.values(DESTINATION_TYPE).includes(destinationType)) {
    errors.push('Select how the destination is reached.');
  }
  if (!CA_POSTAL_RE.test(String(destinationPostalCode).trim())) {
    errors.push('A Canadian postal code is required for the destination.');
  }

  const available = availableDeliveryMethods(destinationType);

  if (Object.values(DESTINATION_TYPE).includes(destinationType) && available.length === 0) {
    errors.push(
      'This destination has no road access, and we have no carrier able to commit to barge or air freight for a vehicle this size. ' +
        'Contact us and we will work the route out with you rather than take a booking we cannot keep.'
    );
  } else if (!Object.values(DELIVERY_METHOD).includes(deliveryMethod)) {
    errors.push('Select a delivery method.');
  } else if (!available.includes(deliveryMethod)) {
    errors.push('That delivery method cannot reach this destination.');
  }

  if (toReserve && !String(reserveName).trim()) {
    // The reserve has to be named: it is what the tax evidence refers to.
    errors.push('Name the reserve the vehicle is being delivered to.');
  }

  if (errors.length) {
    return { valid: false, errors, request: null };
  }

  // Delivery location in the tax module's terms.
  //
  // Only a delivery performed by the seller or their agent can put the vehicle
  // on a reserve. A buyer who collects takes possession at the *seller's*
  // location, so `toReserve` describes where they intend to drive it
  // afterwards — which is not a fact about the sale, and does not support
  // relief. An earlier version of this mapped collection-with-toReserve onto
  // ON_RESERVE_BUYER_COLLECTS and handed back relief for a vehicle the seller
  // never moved; that is the false exemption this whole area is built to
  // avoid.
  //
  // ON_RESERVE_BUYER_COLLECTS is a different fact entirely — the sale itself
  // completes on a reserve, because that is where the seller is. This form
  // does not capture the seller's location, so it never claims it.
  const vendorDelivers = deliveryMethod !== DELIVERY_METHOD.BUYER_COLLECTS;
  const deliveryLocation =
    toReserve && vendorDelivers
      ? DELIVERY_LOCATION.ON_RESERVE_BY_VENDOR
      : DELIVERY_LOCATION.OFF_RESERVE;

  const taxAssessment = assessFirstNationsTax({
    buyerStatus,
    deliveryLocation,
    province,
    documentsOnFile,
    forBandManagement,
  });

  return {
    valid: true,
    errors: [],
    request: {
      kind: 'commercial-delivery-request',
      listingId,
      destinationType,
      destinationPostalCode: String(destinationPostalCode)
        .trim()
        .toUpperCase(),
      deliveryMethod,
      toReserve: !!toReserve,
      reserveName: toReserve ? String(reserveName).trim() : null,
      // Said out loud so nobody downstream treats this as a booking.
      quoted: false,
      costEstimateCents: null,
      disclaimer:
        'A request, not a quote or a booking. A coach is driven or moved by a specialist heavy hauler, ' +
        'not carried on a car transporter, so the cost is sourced per route.',
      taxAssessment,
      mustChargeFullTax: mustChargeFullTax(taxAssessment),
    },
  };
};

// ─── Secure document submission ──────────────────────────────────────────────

export const DOCUMENT_TYPE = {
  BAND_COUNCIL_RESOLUTION: 'band-council-resolution',
  STATUS_CARD: 'status-card',
  BAND_CERTIFICATE: 'band-certificate',
  CVIP_CERTIFICATE: 'cvip-certificate',
  INSURANCE: 'insurance',
  PURCHASE_ORDER: 'purchase-order',
  SERVICE_RECORDS: 'service-records',
};

/**
 * Document types carrying personal or governance information. These are the
 * ones that must never reach a log, an analytics payload, or a URL.
 */
const SENSITIVE = [
  DOCUMENT_TYPE.STATUS_CARD,
  DOCUMENT_TYPE.BAND_COUNCIL_RESOLUTION,
  DOCUMENT_TYPE.BAND_CERTIFICATE,
  DOCUMENT_TYPE.INSURANCE,
];

export const isSensitiveDocument = documentType => SENSITIVE.includes(documentType);

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 15 * 1024 * 1024;

/**
 * Decide whether a document submission may proceed.
 *
 * Authorisation is checked before anything else and fails closed: an
 * unauthenticated caller, or one who is not a party to the listing, is refused
 * without being told whether the listing exists.
 *
 * A status card is the clearest example of why `isSensitiveDocument` matters.
 * It carries a registry number, and it is submitted precisely by the people
 * this marketplace is least able to afford to expose. Callers must consult
 * `storage.logFilename === false` rather than deciding for themselves.
 *
 * @returns {{authorized: Boolean, errors: Array<String>, storage: Object|null}}
 */
export const authorizeDocumentSubmission = ({
  documentType = null,
  mimeType = '',
  sizeBytes = null,
  filename = '',
  submitterId = null,
  listingId = null,
  listingParties = [],
} = {}) => {
  // Authorisation first. A validation message that differs for a listing that
  // exists and one that does not is an enumeration oracle.
  if (!submitterId || !listingId || !listingParties.includes(submitterId)) {
    return {
      authorized: false,
      errors: ['Not authorized to submit documents for this listing.'],
      storage: null,
    };
  }

  const errors = [];
  if (!Object.values(DOCUMENT_TYPE).includes(documentType)) {
    errors.push('Unknown document type.');
  }
  if (!ALLOWED_MIME.includes(mimeType)) {
    errors.push('Documents must be PDF, JPEG, PNG or WebP.');
  }
  if (!Number.isInteger(sizeBytes) || sizeBytes <= 0) {
    errors.push('The file appears to be empty.');
  } else if (sizeBytes > MAX_BYTES) {
    errors.push('Documents must be 15 MB or smaller.');
  }
  if (!String(filename).trim()) {
    errors.push('The file must have a name.');
  }

  if (errors.length) {
    return { authorized: false, errors, storage: null };
  }

  const sensitive = isSensitiveDocument(documentType);

  return {
    authorized: true,
    errors: [],
    storage: {
      documentType,
      sensitive,
      /** Private always. There is no public bucket for any of these. */
      visibility: 'private',
      /**
       * Whether the filename may appear in logs. False for anything sensitive:
       * people name files things like `status-card-<registry number>.pdf`, so
       * the name itself leaks the contents.
       */
      logFilename: !sensitive,
      /** Signed, short-lived access only — never a guessable or durable URL. */
      requiresSignedUrl: true,
      retentionNote: sensitive
        ? 'Retain only while the transaction needs it, then delete. Do not copy into analytics, exports or support tickets.'
        : 'Retain with the transaction record.',
    },
  };
};
